<template>
  <b-card
    border-variant="primary" header-bg-variant="primary" header-text-variant="white"
    header-class="p-3 font-size-lg font-weight-bold"
    header="Premium by state — payable and vehicle exposure"
  >
    <b-alert v-if="holdDetail" variant="warning" show class="mb-3">
      <strong>State premium HOLD:</strong> {{ holdDetail }}
    </b-alert>

    <div v-if="labels" class="small text-muted mb-3">
      <div>
        <strong>{{ labels.underwritingBase || "Underwriting base" }}:</strong>
        {{ formatCents(totals && totals.underwritingBasePremiumCents) }}
      </div>
      <div>
        <strong>{{ labels.annualPayable || "Annual payable (K_StateCharges)" }}:</strong>
        {{ formatCents(totals && totals.annualPayableCents) }}
      </div>
    </div>

    <table v-if="rows.length" class="table table-sm table-bordered mb-0">
      <thead>
        <tr>
          <th class="text-center">State / Jurisdiction</th>
          <th class="text-center">Vehicle class</th>
          <th class="text-center">Count</th>
          <th class="text-center">Base Premium</th>
          <th class="text-center">FSL</th>
          <th class="text-center">Stamp Duty</th>
          <th class="text-center">GST</th>
          <th class="text-center">Annual Premium</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="row in rows">
          <tr :key="row.jurisdiction + '-money'">
            <td class="text-center align-middle" :rowspan="classRowSpan(row)">
              <strong>{{ row.jurisdiction }}</strong>
              <div class="small text-muted" :title="row.contentSha256">
                {{ sourceLabel(row) }}<br>
                observed {{ row.observedAt }}<br>
              </div>
              <div class="small mt-1">
                Vehicles in state: {{ vehicleCountLabel(row.vehicleCountTotal) }}
              </div>
            </td>
            <td class="text-left">
              {{ firstClass(row) ? firstClass(row).className : "—" }}
            </td>
            <td class="text-center">
              {{ firstClass(row) ? firstClass(row).vehicleCount : "—" }}
            </td>
            <td class="text-right">{{ formatCents(row.basePremiumCents) }}</td>
            <td class="text-right">{{ formatCents(row.fireServicesLevyCents) }}</td>
            <td class="text-right">{{ formatCents(row.stampDutyCents) }}</td>
            <td class="text-right">{{ formatCents(row.gstCents) }}</td>
            <td class="text-right">{{ formatCents(row.annualPremiumCents) }}</td>
          </tr>
          <tr
            v-for="(vc, idx) in extraClasses(row)"
            :key="row.jurisdiction + '-class-' + idx"
          >
            <td class="text-left pl-4">{{ vc.className }}</td>
            <td class="text-center">{{ vc.vehicleCount }}</td>
            <td colspan="5" class="text-muted small">Exposure only — money is on the state row above</td>
          </tr>
        </template>
        <tr v-if="totals" class="font-weight-bolder" style="background-color: lightgray">
          <td class="text-center">Total</td>
          <td class="text-center" colspan="2">
            {{ vehicleCountLabel(premiumByState.vehicleGrandTotal) }} vehicles
          </td>
          <td class="text-right">{{ formatCents(totals.basePremiumCents) }}</td>
          <td class="text-right">{{ formatCents(totals.fireServicesLevyCents) }}</td>
          <td class="text-right">{{ formatCents(totals.stampDutyCents) }}</td>
          <td class="text-right">{{ formatCents(totals.gstCents) }}</td>
          <td class="text-right">{{ formatCents(totals.annualPremiumCents) }}</td>
        </tr>
      </tbody>
    </table>
    <div v-else class="text-muted">
      No state premium rows on the accepted quote.
    </div>
  </b-card>
</template>

<script>
export default {
  name: "StatePremiumTable",

  props: {
    premiumByState: {
      type: Object,
      default: null
    }
  },

  computed: {
    rows: function () {
      return (this.premiumByState && Array.isArray(this.premiumByState.rows))
        ? this.premiumByState.rows
        : [];
    },
    totals: function () {
      return (this.premiumByState && this.premiumByState.totals) || null;
    },
    labels: function () {
      return (this.premiumByState && this.premiumByState.labels) || null;
    },
    holdDetail: function () {
      if (!this.premiumByState || !this.premiumByState.reconcile || this.premiumByState.reconcile.pass) {
        return null;
      }
      return this.premiumByState.reconcile.detail;
    }
  },

  methods: {
    classList: function (row) {
      return (row && Array.isArray(row.vehicleClasses)) ? row.vehicleClasses : [];
    },
    firstClass: function (row) {
      const list = this.classList(row);
      return list.length ? list[0] : null;
    },
    extraClasses: function (row) {
      const list = this.classList(row);
      return list.length > 1 ? list.slice(1) : [];
    },
    classRowSpan: function (row) {
      const n = this.classList(row).length;
      return Math.max(1, n);
    },
    formatCents: function (value) {
      if (!Number.isSafeInteger(value)) {
        return "—";
      }
      return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
        minimumFractionDigits: 2
      }).format(value / 100);
    },
    shortHash: function (value) {
      return value ? `${String(value).slice(0, 12)}…` : "missing";
    },
    sourceLabel: function (row) {
      const source = row && row.sourceRecordReference;
      if (!source) {
        return "source missing";
      }
      return `${source.module}/${source.recordId}/${source.rowId}`;
    },
    vehicleCountLabel: function (value) {
      if (value == null || value === "") {
        return "—";
      }
      if (!Number.isFinite(Number(value))) {
        return "—";
      }
      return String(Number(value));
    }
  }
};
</script>
